import { useMutation } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QueryKey } from '@/Config/api/queryKey';
import { KBSourcesStatsDto } from '@/Domain/api/Analytics/dto/KBSources.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/Domain/components/Shared/Card/Card';
import { DateRangeControls } from '@/Domain/components/Shared/DateRangeControls/DateRangeControls';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import {
  useKBSourcesAnalytics,
  useKBSourcesExamples,
} from '@/Domain/hooks/Analytics/useKBSourcesAnalytics.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { ConversationExamplesSheet } from '@/UI/Insights/KBSources/components/ConversationExamplesSheet';
import { KBSourcesTable } from '@/UI/Insights/KBSources/components/KBSourcesTable';
import { useDateRange } from '@/UI/Insights/context/DateRangeContext';

// Mirrors MAX_ANALYTICS_DATE_RANGE_DAYS: the API clamps anything longer to the window's final 30 days.
const MAX_WINDOW_DAYS = 30;

export const KBSourcesPage = () => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'kb_sources' });
  const { range, handleRangeChange } = useDateRange();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([{ id: 'raw_usage_count', desc: true }]);

  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [isExamplesSheetOpen, setIsExamplesSheetOpen] = useState(false);

  const sortKey = 'raw_usage_count' as const;
  const sortDesc = sorting[0]?.desc ?? true;

  // Reset to the first page when the window or sort changes; a stale pageIndex would
  // otherwise request an offset past the new result count and show the empty state
  // (and disable export) even when page 1 has data.
  useEffect(() => {
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [range.startDate?.getTime(), range.endDate?.getTime(), sortDesc]);

  const {
    items,
    totalCount,
    isWindowClamped,
    effectiveStartDate,
    effectiveEndDate,
    totalUsageCount,
    uniqueArticlesCount,
    uniqueConversationsCount,
    kbBackedRepliesPercent,
    topArticleSharePercent,
    currentAutomaticAnalysisSamplingRate,
    isLoading,
    isSuccess,
    error,
  } = useKBSourcesAnalytics({
    range,
    pageIndex: pagination.pageIndex,
    sortKey,
    sortDesc,
  });

  const {
    articleTitle,
    examples,
    isLoading: examplesLoading,
    error: examplesError,
  } = useKBSourcesExamples({
    articleId: selectedArticleId,
    range,
    enabled: isExamplesSheetOpen,
  });

  const exportMutation = useMutation<unknown, Error, { start_date: string; end_date: string }>({
    mutationKey: [QueryKey.export.kbSources],
  });

  const { capture } = usePostHogAnalytics();

  const requestedWindowDays =
    range.startDate && range.endDate
      ? differenceInCalendarDays(range.endDate, range.startDate) + 1
      : null;
  const effectiveWindowDays =
    effectiveStartDate && effectiveEndDate
      ? differenceInCalendarDays(parseISO(effectiveEndDate), parseISO(effectiveStartDate)) + 1
      : null;

  // Fire once per tab view, gated on loaded data so the counts below are real.
  const hasFiredViewRef = useRef(false);
  useEffect(() => {
    if (hasFiredViewRef.current || !isSuccess) return;
    hasFiredViewRef.current = true;
    capture(PostHogEventEnum.KbSourcesTabViewed, {
      has_data: totalCount > 0,
      article_count: totalCount,
      requested_window_days: requestedWindowDays,
      effective_window_days: effectiveWindowDays,
      is_window_clamped: isWindowClamped,
    });
  }, [isSuccess, totalCount, requestedWindowDays, effectiveWindowDays, isWindowClamped, capture]);

  const handleExport = () => {
    const startDate = range.startDate ? format(range.startDate, 'yyyy-MM-dd') : '';
    const endDate = range.endDate ? format(range.endDate, 'yyyy-MM-dd') : '';
    capture(PostHogEventEnum.KbSourcesExportClicked, {
      article_count: totalCount,
      requested_window_days: requestedWindowDays,
      effective_window_days: effectiveWindowDays,
    });
    exportMutation.mutate({ start_date: startDate, end_date: endDate });
  };

  const handleViewExamples = (articleId: number) => {
    capture(PostHogEventEnum.KbSourcesExamplesOpened, { article_id: articleId });
    setSelectedArticleId(articleId);
    setIsExamplesSheetOpen(true);
  };

  const handleCloseExamples = () => {
    setIsExamplesSheetOpen(false);
    setSelectedArticleId(null);
  };

  const data: KBSourcesStatsDto | undefined =
    items.length > 0 || isLoading
      ? {
          items,
          count: totalCount,
          offset: pagination.pageIndex * pagination.pageSize,
          total_usage_count: totalUsageCount,
          unique_articles_count: uniqueArticlesCount,
          unique_conversations_count: uniqueConversationsCount,
          kb_backed_replies_percent: kbBackedRepliesPercent,
          top_article_share_percent: topArticleSharePercent,
          start_date: effectiveStartDate ?? '',
          end_date: effectiveEndDate ?? '',
        }
      : undefined;

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-6">
      {/* Date Range Controls */}
      <DateRangeControls
        range={range}
        onRangeChange={handleRangeChange}
        maxRangeDays={MAX_WINDOW_DAYS}
      />

      {isWindowClamped && (
        <div
          data-testid="kb-sources-clamped-notice"
          className="flex items-start gap-2 rounded-xl border border-border bg-muted px-3.5 py-2.5 text-sm text-muted-foreground"
        >
          <Info className="size-4 shrink-0 mt-0.5" />
          <span>{t('clamped_window', { start: effectiveStartDate, end: effectiveEndDate })}</span>
        </div>
      )}

      {/* Main Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle>{t('title')}</CardTitle>
            <CardDescription>{t('description')}</CardDescription>
            {/* Absent only mid-rollout, and the tooltips already carry the observed caveat. */}
            {isSuccess &&
              currentAutomaticAnalysisSamplingRate !== null &&
              currentAutomaticAnalysisSamplingRate < 1 && (
                <div
                  data-testid="kb-sources-sampling-notice"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <Info className="size-3.5 shrink-0" aria-hidden="true" />
                  <span>
                    {t('sampling_notice', {
                      percentage: Math.round(currentAutomaticAnalysisSamplingRate * 100),
                    })}
                  </span>
                </div>
              )}
          </div>
          <Button
            data-testid="kb-sources-export-button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exportMutation.isPending || !data?.items.length}
          >
            {exportMutation.isPending ? t('exporting') : t('export')}
          </Button>
        </CardHeader>

        <KBSourcesTable
          data={data}
          isLoading={isLoading}
          isError={!!error}
          sorting={sorting}
          setSorting={setSorting}
          pagination={pagination}
          setPagination={setPagination}
          onViewExamples={handleViewExamples}
        />
      </Card>

      <ConversationExamplesSheet
        isOpen={isExamplesSheetOpen}
        onClose={handleCloseExamples}
        articleTitle={articleTitle}
        examples={examples}
        isLoading={examplesLoading}
        isError={!!examplesError}
      />
    </div>
  );
};
